import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { KeyRound, Check, X } from "lucide-react";
import api from "@/lib/api";

function getPasswordStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const tr = (key, fallback) => { const v = t(key); return v === key ? fallback : v; };

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-reset", { email, frontend_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  const strengthLabels = [t("reset.strength_very_weak"), t("reset.strength_weak"), t("reset.strength_fair"), t("reset.strength_strong"), t("reset.strength_very_strong")];
  const strengthColors = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-500"];

  const strength = getPasswordStrength(password);
  const matches = password && confirm && password === confirm;
  const valid = password.length >= 8 && matches && strength >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success(t("reset.success_title"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block"><img src="/logo.jpg" alt="Smartpriced" className="h-12 mx-auto" /></Link>
          </div>
          <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl flex items-center justify-center gap-2"><KeyRound className="w-5 h-5" />{tr("forgot.title", "Wachtwoord vergeten")}</CardTitle>
              <CardDescription>{tr("forgot.subtitle", "Vul je e-mailadres in en we sturen je een link om je wachtwoord te herstellen.")}</CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <p className="text-center text-sm text-muted-foreground py-4" data-testid="forgot-sent">{tr("forgot.sent", "Als dit e-mailadres bij ons bekend is, ontvang je binnen enkele minuten een e-mail met een resetlink.")}</p>
              ) : (
                <form onSubmit={handleRequest} className="space-y-4">
                  <div>
                    <Label>{t("form.email")} *</Label>
                    <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="je@email.com" className="mt-1" data-testid="forgot-email-input" />
                  </div>
                  <Button type="submit" className="w-full bg-[#2CA8AC] hover:bg-[#248F92] h-11" disabled={loading || !email} data-testid="forgot-submit-btn">
                    {loading ? t("reset.submitting") : tr("forgot.submit", "Verstuur resetlink")}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[#2CA8AC] hover:text-[#248F92] font-medium">{t("reset.back_login")}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-emerald-600" /></div>
          <h2 className="text-xl font-bold mb-2">{t("reset.success_title")}</h2>
          <p className="text-muted-foreground mb-6">{t("reset.success_desc")}</p>
          <Link to="/login"><Button className="bg-blue-600 hover:bg-blue-700" data-testid="go-to-login-btn">{t("reset.go_login")}</Button></Link>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center"><span className="text-white font-bold">SP</span></div>
            <span className="font-bold text-2xl text-slate-900 tracking-tight">Smartpriced</span>
          </Link>
        </div>

        <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl flex items-center justify-center gap-2"><KeyRound className="w-5 h-5" />{t("reset.title")}</CardTitle>
            <CardDescription>{t("reset.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t("reset.new_password")} *</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("reset.min_chars")} className="mt-1" data-testid="new-password-input" />
                {password && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Progress value={strength * 20} className="h-1.5 flex-1" />
                      <span className={`text-xs font-medium ${strength >= 3 ? "text-emerald-500" : strength >= 2 ? "text-amber-500" : "text-red-500"}`}>
                        {strengthLabels[strength - 1] || strengthLabels[0]}
                      </span>
                    </div>
                    <ul className="text-xs space-y-0.5">
                      {[
                        { ok: password.length >= 8, text: t("reset.req_length") },
                        { ok: /[A-Z]/.test(password), text: t("reset.req_upper") },
                        { ok: /[0-9]/.test(password), text: t("reset.req_number") },
                        { ok: /[^A-Za-z0-9]/.test(password), text: t("reset.req_special") },
                      ].map((r, i) => (
                        <li key={i} className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-500" : "text-muted-foreground"}`}>
                          {r.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {r.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <Label>{t("reset.confirm")} *</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("reset.repeat")} className="mt-1" data-testid="confirm-password-input" />
                {confirm && !matches && <p className="text-xs text-red-500 mt-1">{t("reset.no_match")}</p>}
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11" disabled={!valid || loading} data-testid="reset-password-btn">
                {loading ? t("reset.submitting") : t("reset.submit")}
              </Button>
            </form>
          </CardContent>
        </Card>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium">{t("reset.back_login")}</Link>
        </div>
      </div>
    </div>
  );
}
